import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, catchError, tap } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { HttpParams } from '@angular/common/http';

import { JsonService } from '../_services/json.service';
import { ConfirmationDialogService } from '../_services/confirmation-dialog.service';
import { DialogService } from '../dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { StringSwitchService } from '../_helpers/string-switch.service';
import { LoadingService } from '../_services/loading.service';
import { LoggerService } from '../_services/logger.service';
import { GlobalService } from '../_services/global.service';
import { SharedService } from '../_services/shared.service';

@Component({
  selector: 'app-listagem',
  templateUrl: './listagem.component.html',
  styleUrls: ['./listagem.component.scss']
})
export class ListagemComponent implements OnInit {
  listaItens: any[] = [];
  listaShowItens: any[] = [];

  currentPage = 1;
  pageSize = 15;
  totalItems = 0;

  listaStatus: any[] = [];
  listaPaises: any[] = [];
  listaCidades: any[] = [];
  listaPDVs: any[] = [];
  listaAno: any[] = [];
  listaMes: any[] = [];
  listaRegiao: any[] = [];
  listaEnvio: any[] = [];
  listaProvedor: any[] = [];
  listaGrupos: any[] = [];
  jsonPaises: any[] = [];

  itensSelecionados: any[] = [];
  selecionarTodosShow = false;
  isModalOpen = false;
  isAdminUser = false;
  loading: boolean = false;

  filenameFiltrado: string[] = [];
  pdvFiltrado: string[] = [];

  searchControl = new FormControl();
  pdvControl = new FormControl();

  filtro: any = {
    status: 'W', usuario: '', data: '', pais: '', cidade: '',
    pdv: '', ano: '', mes: '', regiao: '', provedor: '',
    envio: '', grupos: '', selecionarTodos: '', responsavel: '', filename: ''
  };

  @ViewChild('csv_buttons', { static: true }) csv_buttons!: ElementRef<HTMLDivElement>;

  constructor(
    private jsonService: JsonService,
    private confirmationDialog: ConfirmationDialogService,
    private dialogService: DialogService,
    private router: Router,
    private loadingService: LoadingService,
    private stringSwitchService: StringSwitchService,
    private translate: TranslateService,
    private logger: LoggerService,
    private globalService: GlobalService,
    private sharedService: SharedService
  ) { }

  ngOnInit(): void {
    this.loadingService.show();
    this.restoreFiltro();

    this.isAdminUser = this.globalService.isAdmin();
    this.sharedService.isAdmin$.subscribe(v => this.isAdminUser = v);

    this.jsonService.unlockAllPage().subscribe({
      next: () => this.loadingService.hide(),
      error: err => { this.logger.error('Erro ao desbloquear página', err); this.loadingService.hide(); }
    });

    this.loadFilters();
    this.setupSearch(this.searchControl, term => this.jsonService.getSearchFilename(term), res => this.filenameFiltrado = res);
    this.setupSearch(this.pdvControl, term => this.jsonService.getSearchPdv(term), res => this.pdvFiltrado = res);
  }

  private setupSearch(control: FormControl, serviceFn: (term: string) => any, updateFn: (res: any) => void) {
    control.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => !term?.trim() ? of([]) : serviceFn(term).pipe(
        tap((resp: any) => {
          updateFn(resp);
        }),
        catchError(err => {
          this.logger.error('Erro na busca', err);
          return of([]);
        })
      ))
    ).subscribe();
  }

  private restoreFiltro() {
    const saved = localStorage.getItem('filtro');
    this.filtro = saved ? JSON.parse(saved) : this.filtro;
    if (!saved) localStorage.setItem('filtro', JSON.stringify(this.filtro));
  }

  loadPage(page: number): void {
    let params = new HttpParams()
      .append('PageNumber', page)
      .append('pageSize', this.pageSize);

    Object.entries(this.filtro).forEach(([k, v]) => {
      params = params.append(k === 'regiao' ? 'region' : k, String(v));
    });

    this.jsonService.getPaginatedData(params).subscribe({
      next: resp => {
        this.listaShowItens = resp.items;
        this.totalItems = resp.totalItems;
        this.currentPage = page;
        this.loadingService.hide();
      },
      error: err => this.logger.error('Erro ao carregar página', err)
    });
  }

  onPageChange(page: number) {
    this.loadingService.show();
    this.loadPage(page);
  }

  editarReceita(item: any) {
    if (item.sendoAcessado) {
      this.dialogService.abrirDialog('aviso', this.t('Aviso'), this.t('Receita já está sendo acessada por outro usuário!'));
      return;
    }
    const formData = new FormData();
    formData.append('status', '1');
    formData.append('prescriptionId', item.id);
    this.jsonService.lockPageCreate(formData).subscribe({
      next: () => this.router.navigate(['/receita', item.id]),
      error: err => this.logger.error('Erro ao editar receita', err)
    });
  }

  sendCaptura() {
    this.isModalOpen = false;
    this.loadingService.show();
    this.jsonService.sendCaptura().subscribe({
      next: resp => this.handleCapturaResponse(resp),
      error: err => this.showError('Erro ao enviar captura', err)
    });
  }

  private handleCapturaResponse(resp: any) {
    if (!resp.isSuccess) {
      this.dialogService.abrirDialog('aviso', this.t('Sucesso'), this.t('¡Captura completada con éxito! Los arquivos se crearán pronto.'));
      resp.forEach((item: any) => this.downloadFile(item['pais'], item['bytes']));
    } else {
      this.dialogService.abrirDialog('aviso', this.t('Error'), this.t('Nenhuma prescrição foi revisada nas últimas 24 horas.'));
    }
    this.loadingService.hide();
  }

  private showError(msg: string, err: any) {
    this.logger.error(msg, err);
    this.dialogService.abrirDialog('aviso', this.t('Error'), this.t('Ocorreu um erro inesperado, favor tentar novamente!'));
    this.loadingService.hide();
  }

  downloadFile(titulo: string, bytesBase64: string) {
    try {
      const clean = bytesBase64.replace(/\s/g, '').replace(/[^A-Za-z0-9+/=]/g, '');
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) { bytes[i] = binary.charCodeAt(i); }
      const text = new TextDecoder('utf-8').decode(bytes);
      const sanitized = this.sanitizeCsvPipes(text);
      const blob = new Blob([sanitized], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${titulo}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (e) {
      this.logger.error('Erro ao processar Base64', e);
    }
  }

  private sanitizeCsvPipes(text: string): string {
    const lines = text.split(/\r?\n/);
    if (!lines.length) return text;
    const headerPipes = (lines[0].match(/\|/g) ?? []).length;
    return lines.map((line, i) => {
      if (i === 0 || !line.trim()) return line;
      const linePipes = (line.match(/\|/g) ?? []).length;
      if (linePipes <= headerPipes) return line;
      let seen = 0;
      return [...line].map(ch => {
        if (ch !== '|') return ch;
        return ++seen <= headerPipes ? '|' : '';
      }).join('');
    }).join('\n');
  }

  private loadFilters() {
    forkJoin({
      paises: this.jsonService.listCountry(),
      pdvs: this.jsonService.getPDVs(),
      anos: this.jsonService.getAno(),
      meses: this.jsonService.getMes(),
      regioes: this.jsonService.getRegiao(),
      provedores: this.jsonService.getProvedor(),
      envios: this.jsonService.getEnvio(),
      grupos: this.jsonService.getGroupList(),
      status: this.jsonService.getStatus()
    }).subscribe({
      next: data => {
        this.listaPaises = data.paises;
        this.listaPDVs = data.pdvs;
        this.listaAno = data.anos;
        this.listaMes = data.meses;
        this.listaRegiao = data.regioes;
        this.listaProvedor = data.provedores;
        this.listaEnvio = data.envios;
        this.listaGrupos = data.grupos;
        this.listaStatus = data.status.filter((s: any) => s !== 'W' && s !== 'E');
        this.jsonPaises = data.paises;
      },
      error: err => this.logger.error('Erro ao carregar filtros', err)
    });
  }

  atualizaFiltro() {
    this.filtro.data = this.formatDate(this.filtro.dataf);
    localStorage.setItem('filtro', JSON.stringify(this.filtro));
    this.onPageChange(1);
    this.atualizaSelecionado();
  }

  limparFiltro() {
    localStorage.removeItem('filtro');
    this.filtro = { status: '', usuario: '', data: '', pais: '', cidade: '', pdv: '', ano: '', mes: '', regiao: '', provedor: '', envio: '', filename: '' };
    this.listaShowItens = [];
    this.totalItems = 0;
    this.loadingService.hide();
  }

  // Helpers
  private t(key: string) { return this.translate.instant(key) + '!'; }
  formatDate(date: string): string { return date ? date.split('-').reverse().join('/') : ''; }
  switchString(value: string) { return this.stringSwitchService.switchString(value); }

  arredondar(valor: number): number {
    return Math.ceil(valor);
  }
  getCountry(countryId: string): string {
    if (!this.listaPaises || this.listaPaises.length === 0) return countryId;
    const pais = this.listaPaises.find(p => p.id === countryId);
    return pais ? pais.name : countryId;
  }

  getRegionName(regionId: string): string {
    if (!this.listaRegiao || this.listaRegiao.length === 0) return regionId;
    const regiao = this.listaRegiao.find(r => r.id === regionId);
    return regiao ? regiao.name : regionId;
  }
  retornaGroupName(groupId: string): string {
    if (!this.listaGrupos || this.listaGrupos.length === 0) {
      return groupId;
    }

    const grupo = this.listaGrupos.find(g => g.id === groupId);
    return grupo ? grupo.name : groupId;
  }

  toggleSelecionarTodos() {
    this.selecionarTodosShow = !this.selecionarTodosShow;

    if (this.selecionarTodosShow) {
      this.listaShowItens.forEach(item => item.selecionado = true);
    } else {
      this.listaShowItens.forEach(item => item.selecionado = false);
    }

    this.atualizaSelecionado();
  }

  atualizaSelecionado() {
    this.itensSelecionados = this.listaShowItens.filter(item => item.selecionado);
    if(this.itensSelecionados.length <= 0) {
      this.selecionarTodosShow = false;
    }else{
      this.selecionarTodosShow = true;
    }
    console.log(this.itensSelecionados);
  }

  openModal(): void {
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }
  
  async changeGroup() {
    this.itensSelecionados = this.listaShowItens.filter(item => item.selecionado);
    if(this.itensSelecionados.length >= 1) {
      this.selecionarTodosShow = true;
    }else{
      this.selecionarTodosShow = false;
    }
    const selectedItems = this.itensSelecionados;
    let finalArray : any = {};
    let itensArray : Array<any> = [];
    selectedItems.forEach(item => {
      itensArray.push(item.id);
    });
    finalArray['newGroupId'] = this.filtro.grupos;
    finalArray['prescriptionIds'] = itensArray;
    const confirmed = await this.confirmationDialog.confirm(
      'Confirmação',
      'Tem certeza de que deseja alterar o grupo destes itens?'
    );

    if (confirmed) {
      this.loadingService.show();
      this.chageGroupSend(finalArray);
    } else {
      //
    }      
  }

  chageGroupSend(array : any) {
    this.jsonService.changeGroup(array).subscribe(
      resp => {
        this.loadingService.hide();
        let title = this.translate.instant('Sucesso') + '!';
        let texto = this.translate.instant('Receita publicada para análise!');
        this.dialogService.abrirDialog('aviso', title, texto);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      },
      erro => {
        this.logger.error('Erro ao alterar grupo', erro);
      }
    );
  }

  async atualizaResponsavel(item: any) {
    let finalArray : any = {};
    let itensArray : Array<any> = [item.id];
    finalArray['newGroupId'] = item.groupId;
    finalArray['prescriptionIds'] = itensArray;

    const confirmed = await this.confirmationDialog.confirm(
      'Confirmação',
      'Tem certeza de que deseja alterar o grupo deste item?'
    );

    if (confirmed) {
      this.chageGroupSend(finalArray);
    } else {
      //
    }
    
  }
}
